import type {Instant, UUID} from './common'
import type {AttributeValueType} from './attribute'
import type {SetAttributeRequest} from './attribute-definition'
import type {OwnerResponse} from './owner'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const TerminationAction = {
    DELETE: 'DELETE',
    DISABLE: 'DISABLE',
} as const;
export type TerminationAction = typeof TerminationAction[keyof typeof TerminationAction];
/* eslint-enable i18next/no-literal-string */

/**
 * Target system response from the API.
 */
export interface TargetSystem {
  id: UUID;
  systemType: string;
  environment: string;
  name: string;
  description: string | null;
  externalId: string | null;
  authoritativeForIdentities: boolean;
  authoritativeForAttributes: boolean;
  authoritativeForAccounts: boolean;
  terminationAction: TerminationAction;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Request payload for POST /api/i/target-systems (issue 743).
 */
export interface TargetSystemCreateRequest {
  systemType: string;
  environment: string;
  name: string;
  externalId?: string | null;
  description?: string | null;
  attributes: SetAttributeRequest[];
}

/**
 * Target system summary for embedding in other responses.
 */
export interface SystemSummary {
  id: UUID;
  name: string;
  systemType: string;
  environment: string;
}

/**
 * Paginated response for systems.
 */
export interface SystemPage {
  content: TargetSystem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface TargetSystemOwner extends OwnerResponse {
    targetSystemId: UUID
}

/**
 * Request payload for PUT /api/i/target-systems/{id} (issue 873).
 * Per-field edit dialog sends the new name + loaded version.
 */
export interface TargetSystemUpdateRequest {
    name: string
    version: number
}

export type {AssignOwnerRequest} from './owner'

export interface Attribute {
    id: UUID
    definitionId: UUID
    name: string
    displayName: string
    valueType: AttributeValueType
    value: unknown
    values?: string[]
    authorityId: UUID | null
    authorityName: string | null
    allowedOperations: string[]
    version: number
    observedAt?: Instant
    createdAt: Instant
    updatedAt: Instant
}
